import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_TYPOGRAPHY_SETTINGS,
  FIXED_MONO_FONT_ID,
  FIXED_TITLE_FONT_ID,
  TYPOGRAPHY_STORAGE_KEY,
  findFont,
  findFontForRole,
  getPresetSettings,
  normalizeTypographySettings,
  type TypographySettings,
} from './typography.system';

interface TypographyContextValue {
  settings: TypographySettings;
  updateSettings: (updates: Partial<TypographySettings>) => void;
  applyPreset: (presetId: string) => void;
  resetTypography: () => void;
}

const TypographyContext = createContext<TypographyContextValue | null>(null);

function readStoredSettings() {
  if (typeof window === 'undefined') {
    return DEFAULT_TYPOGRAPHY_SETTINGS;
  }

  try {
    const stored = window.localStorage.getItem(TYPOGRAPHY_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_TYPOGRAPHY_SETTINGS;
    }

    return normalizeTypographySettings(JSON.parse(stored));
  } catch {
    return DEFAULT_TYPOGRAPHY_SETTINGS;
  }
}

function applyTypographySettings(settings: TypographySettings) {
  const root = document.documentElement;
  const normalizedSettings = normalizeTypographySettings(settings);
  const titleFont = findFont(FIXED_TITLE_FONT_ID);
  const bodyFont = findFontForRole(normalizedSettings.bodyFontId, 'body');
  const buttonFont = findFontForRole(normalizedSettings.buttonFontId, 'button');
  const monoFont = findFont(FIXED_MONO_FONT_ID);
  const mediumBodyWeight = Math.min(700, normalizedSettings.bodyFontWeight + 100);
  const strongBodyWeight = Math.min(700, normalizedSettings.bodyFontWeight + 200);

  root.style.setProperty('--font-title', titleFont.stack);
  root.style.setProperty('--font-body', bodyFont.stack);
  root.style.setProperty('--font-button', buttonFont.stack);
  root.style.setProperty('--font-mono', monoFont.stack);
  root.style.setProperty('--font-display', titleFont.stack);
  root.style.setProperty('--font-sans', bodyFont.stack);
  root.style.setProperty('--font-title-letter-spacing', `${normalizedSettings.titleLetterSpacing}em`);
  root.style.setProperty('--font-body-letter-spacing', `${normalizedSettings.bodyLetterSpacing}em`);
  root.style.setProperty('--font-body-weight', `${normalizedSettings.bodyFontWeight}`);
  root.style.setProperty('--font-body-medium-weight', `${mediumBodyWeight}`);
  root.style.setProperty('--font-body-strong-weight', `${strongBodyWeight}`);
  root.style.setProperty('--font-body-size', `${normalizedSettings.bodyFontSize}px`);
  root.style.setProperty('--font-body-lg-size', `${normalizedSettings.bodyFontSize + 4}px`);
  root.style.setProperty('--font-body-sm-size', `${Math.max(12, normalizedSettings.bodyFontSize - 2)}px`);
  root.style.setProperty('--font-caption-size', `${Math.max(11, normalizedSettings.bodyFontSize - 4)}px`);
  root.style.setProperty('--font-button-size', `${Math.max(13, normalizedSettings.bodyFontSize - 2)}px`);
  root.style.setProperty('--font-button-sm-size', `${Math.max(12, normalizedSettings.bodyFontSize - 3)}px`);
  root.style.setProperty('--font-button-lg-size', `${Math.max(14, normalizedSettings.bodyFontSize - 1)}px`);
  root.dataset.typePreset = normalizedSettings.presetId;
}

export function TypographyProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<TypographySettings>(readStoredSettings);

  useEffect(() => {
    applyTypographySettings(settings);
    window.localStorage.setItem(TYPOGRAPHY_STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const value = useMemo<TypographyContextValue>(() => ({
    settings,
    updateSettings: (updates) => {
      setSettings((current) => normalizeTypographySettings({
        ...current,
        presetId: 'custom',
        ...updates,
      }));
    },
    applyPreset: (presetId) => setSettings(getPresetSettings(presetId)),
    resetTypography: () => setSettings(DEFAULT_TYPOGRAPHY_SETTINGS),
  }), [settings]);

  return (
    <TypographyContext.Provider value={value}>
      {children}
    </TypographyContext.Provider>
  );
}

export function useTypography() {
  const context = useContext(TypographyContext);
  if (!context) {
    throw new Error('useTypography must be used inside TypographyProvider');
  }

  return context;
}
